import "moment/locale/en-sg";

import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom } from "@angular/core";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { provideRouter } from "@angular/router";
import { NX_DATE_LOCALE } from "@aposin/ng-aquila/datefield";
import { NxModalModule } from "@aposin/ng-aquila/modal";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { NgxsReduxDevtoolsPluginModule } from "@ngxs/devtools-plugin";
import { NgxsLoggerPluginModule } from "@ngxs/logger-plugin";
import { NgxsModule } from "@ngxs/store";
import { AuthSessionFacade } from "@services/auth-session/auth-session.facade";
import { CredentialsInterceptorService } from "@services/credentials-interceptor/credentials-interceptor.service";
import { LoadingInterceptorService } from "@services/loading-interceptor/loading-interceptor.service";
import { RequestInterceptorService } from "@services/request-interceptor/request-interceptor.service";
import { UserService } from "@shared/services/user/user.service";
import { DashboardService } from "@store/dashboard/dashboard.service";
import { DashboardState } from "@store/dashboard/dashboard.state";
import { LoginState } from "@store/login/login.state";
import { NotificationService } from "@store/notification/notification.service";
import { ProfileService } from "@store/profile/profile.service";
import { ProfileState } from "@store/profile/profile.state";
import { firstValueFrom } from "rxjs";

import { environment as env } from "../environments/environment";
import { routes } from "./app.routes";
import { UserState } from "./store/user/user.state";

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, "./assets/i18n/", ".json?4");
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    // Synchronise with server time BEFORE the router activates any guarded route
    // (and before restorePersistedSession), so token-expiry checks use server
    // time instead of a possibly-wrong device clock and never wrongly clear the
    // persisted session. syncServerTime() is bounded by an internal timeout and
    // never rejects, so it cannot stall bootstrap.
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AuthSessionFacade],
      useFactory: (authSessionFacade: AuthSessionFacade) => () =>
        firstValueFrom(authSessionFacade.syncServerTime())
          .then(() => {
            authSessionFacade.restorePersistedSession();
          })
          .catch(() => {
            // Never block bootstrap on the time sync / restore.
          }),
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptorService,
      multi: true,
    },
    importProvidersFrom([BrowserAnimationsModule]),
    importProvidersFrom(
      NgxsModule.forRoot(
        [UserState, DashboardState, LoginState, ProfileState],
        {
          developmentMode: !env.production,
        },
      ),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled: env.production, // Disable logging in production
      }),
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    { provide: NX_DATE_LOCALE, useValue: "en-sg" },
    UserService,
    DashboardService,
    NotificationService,
    ProfileService,
    importProvidersFrom(NxModalModule.forRoot()),
  ],
};
